import { ChangeDetectionStrategy, Component, computed, effect, input, output, signal } from '@angular/core';
import { GSIconListBulletModel, GSIconListModel } from './gs-icon-list.model';

@Component({
  selector: 'gs-icon-list',
  standalone: true,
  imports: [],
  templateUrl: './gs-icon-list.component.html',
  styleUrls: ['./gs-icon-list.component.css'],
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class GsIconListComponent {
  readonly listData = input<GSIconListModel>(new GSIconListModel(), { alias: 'ListData' });
  readonly debugData = output<string>({ alias: 'DebugData' });
  readonly dataValid = signal(false);
  readonly viewData = signal<GSIconListModel>(new GSIconListModel());
  readonly bullets = computed(() => this.viewData().BulletList);

  constructor() {
    effect(() => {
      const incomingData = this.listData();
      this.debugData.emit(`input data: ${JSON.stringify(incomingData)}`);
      this.validateData(incomingData);
    });
  }

  private validateData(listData: GSIconListModel): void {
    this.dataValid.set(false);
    this.viewData.set(new GSIconListModel());

    const normalizedListData = this.normalizeListData(listData);
    const idRegex = /[^a-zA-Z\d-]/g;
    if (!this.isNullOrWhitespace(normalizedListData.Id) && idRegex.test(normalizedListData.Id.trim())) {
      this.debugData.emit(`ID '${normalizedListData.Id}' is invalid`);
      return;
    }

    if (normalizedListData.BulletList.length < 1) {
      this.debugData.emit('No bullet data provided');
      return;
    }

    const bulletsAreValid = this.validateBullets(normalizedListData.BulletList);
    this.dataValid.set(bulletsAreValid);

    if (bulletsAreValid) {
      this.viewData.set(normalizedListData);
    }

    this.debugData.emit(`Data is valid: ${bulletsAreValid}`);
  }

  private validateBullets(bulletList: GSIconListBulletModel[]): boolean {
    for (let i = 0; i < bulletList?.length; i++) {
      const b = bulletList[i];
      if (this.isNullOrWhitespace(b.IconPath)) {
        this.debugData.emit(`No icon path provided for bullet ${i}`);
        return false;
      }

      if (this.isNullOrWhitespace(b.BulletTitle) && this.isNullOrWhitespace(b.BulletText)) {
        this.debugData.emit(`No title or text provided for bullet ${i}`);
        return false;
      }
    }

    return true;
  }

  private normalizeListData(listData: GSIconListModel | null | undefined): GSIconListModel {
    const normalized = new GSIconListModel();
    normalized.Id = listData?.Id?.trim() ?? '';
    normalized.Header = listData?.Header?.trim() ?? '';
    normalized.Subheader = listData?.Subheader?.trim() ?? '';
    normalized.BulletList = (listData?.BulletList ?? []).map((bullet) => {
      const normalizedBullet = new GSIconListBulletModel();
      normalizedBullet.IconPath = bullet?.IconPath?.trim() ?? '';
      normalizedBullet.IconAltText = bullet?.IconAltText?.trim() ?? '';
      normalizedBullet.BulletTitle = bullet?.BulletTitle?.trim() ?? '';
      normalizedBullet.BulletText = bullet?.BulletText?.trim() ?? '';
      normalizedBullet.ShowBullet = bullet?.ShowBullet ?? true;
      return normalizedBullet;
    });
    return normalized;
  }

  private isNullOrWhitespace(s: string | null | undefined): boolean {
    return s === null || s === undefined || s.trim().length === 0;
  }
}
